import {CTA} from '@volkswagen-onehub/components-core'
import {useTranslation} from 'react-i18next'
import React, {useEffect, useState} from 'react'
import {Global, ROUTE_PATHS} from '../../shared/variables'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from '../rootReducer'
import {useFeatureServices} from '../../use-feature-services'
import {SingleSimulation} from '../../types/SingleSimulation'
import {vehicleTracking} from '../../shared/trackingServiceHelper'
import {appendParamsToUrl} from '../../shared/utilities'
import {
    DealerFormSelection,
    saveDealerFormSelection,
} from '../../shared/dealerFormSelectionStorage'
import {VisuallyHidden} from '../../shared/styled'
import {
    setDealerImageListing,
    setDealerSimulation,
    setPromoIndex,
} from '../../features/dealerFormData/dealerFormDataSlice'
import {AppDispatch} from '../thunk'

interface SimulationButtonsProps {
    promoData: SingleSimulation
    image: string
    modelName: string
    infoCtaUrl: string
    infoCtaUrlParamsPassThrough: boolean
    promoIndex?: number
}

export function SimulationButtons(
    props: SimulationButtonsProps
): React.JSX.Element {
    const {t} = useTranslation('fa-flotte')
    const {'s2:history': historyService} = useFeatureServices() as any
    const dispatch = useDispatch() as AppDispatch

    const serviceType = useSelector(
        (state: RootState) => state.general.serviceType
    )

    const allowedParams = useSelector(
        (state: RootState) => state.general.ctaAllowedParams
    )

    const [infoCtaUrl, setInfoCtaUrl] = useState('')

    const isLeasing = serviceType === Global.LEASING

    const rentCtaHref = historyService.history.createHref
        ? historyService.history.createHref({pathname: ROUTE_PATHS.dealerForm})
        : ROUTE_PATHS.dealerForm

    const onRentClick = () => {
        const promoIndex = props.promoIndex ?? Global.ZERO
        const dealerFormSelection: DealerFormSelection = {
            dealerSimulation: props.promoData,
            dealerImageListing: props.image,
            promoIndex,
        }

        saveDealerFormSelection(dealerFormSelection)

        // Make sure the redux store reflects the simulation the user has
        // just clicked. Without this, a previously stored simulation in
        // state.dealerFormData.dealerSimulation would take precedence in
        // <DealerForm /> and the wrong promo would be shown.
        dispatch(setDealerSimulation(props.promoData))
        dispatch(setDealerImageListing(props.image))
        dispatch(setPromoIndex(promoIndex))
    }

    const navigateToRent = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault()
        onRentClick()
        vehicleTracking(
            Global.BUTTON,
            props.promoData.model,
            props.promoData.version,
            props.promoData.monthly_payment,
            window.location.toString(),
            t('cta.rent')
        )

        if (!historyService.history?.push) {
            window.location.assign(rentCtaHref)

            return
        }

        historyService.history.push(ROUTE_PATHS.dealerForm)

        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        })
    }

    useEffect(() => {
        if (allowedParams.allowed_url_parameters) {
            allowedParams.allowed_url_parameters.forEach(
                (param: {name: string; default_value: string}) => {
                    const urlSearchParams = new URLSearchParams(
                        window.location.search
                    )
                    // Be resilient to the CMS adding/removing the `-app`
                    // suffix on query keys: try the suffixed key first,
                    // fall back to the plain one.
                    const getUrlParam =
                        urlSearchParams.get(`${param.name}-app`) ??
                        urlSearchParams.get(param.name)

                    if (props.infoCtaUrlParamsPassThrough) {
                        const composedParams = []

                        if (getUrlParam) {
                            composedParams.push(
                                `${param.name}${Global.EQUAL}${getUrlParam}`
                            )
                        } else {
                            composedParams.push(
                                `${param.name}${Global.EQUAL}${param.default_value}`
                            )
                        }

                        setInfoCtaUrl(
                            appendParamsToUrl(props.infoCtaUrl, composedParams)
                        )
                    } else {
                        setInfoCtaUrl(props.infoCtaUrl)
                    }
                }
            )
        }
    }, [allowedParams])

    return (
        <>
            <CTA
                tag="a"
                emphasis="primary"
                href={infoCtaUrl}
                stretchContent
                target="_blank"
                onClick={() => {
                    vehicleTracking(
                        Global.BUTTON,
                        props.promoData.model,
                        props.promoData.version,
                        props.promoData.monthly_payment,
                        props.infoCtaUrl,
                        t('cta.info')
                    )
                }}
            >
                {t('cta.info')}
                <VisuallyHidden>
                    {` su ${props.modelName} ${props.promoData.version}`}
                </VisuallyHidden>
            </CTA>

            {!isLeasing && props.promoData.hasFinancialOffer && (
                <CTA
                    tag="a"
                    emphasis="secondary"
                    href={rentCtaHref}
                    aria-label={t('cta.rent')}
                    stretchContent
                    onClick={navigateToRent}
                >
                    {t('cta.rent')}
                </CTA>
            )}
        </>
    )
}
